import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import { useApp } from "@/store/useApp";
import { ShoppingBag, Package, ClipboardList, User, LogOut, Menu } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { formatQuantity } from "@/lib/number";
import { StatusBadge } from "./StatusBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { CartDrawer } from "./CartDrawer";
import { DeliveryUnitSwitcher } from "./DeliveryUnitSwitcher";
import { isTitularAccount } from "@/lib/types";

const CATALOG_NAV = { to: "/catalogo", label: "Catálogo", icon: Package };
const ORDERS_NAV = { to: "/pedidos", label: "Meus Pedidos", icon: ClipboardList };

export function TopNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { account, cart, logout, cartOpen, setCartOpen } = useApp();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Titular é âncora comercial read-only: sem catálogo/carrinho/troca de unidade (só pedidos/ficha).
  const titular = isTitularAccount(account);
  const NAV = titular ? [ORDERS_NAV] : [CATALOG_NAV, ORDERS_NAV];

  const cartCount = cart.reduce((acc, i) => acc + i.quantity, 0);
  const displayName = account?.profile?.companyName ?? account?.email ?? "NA";
  const initials = displayName.slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-surface-elevated/90 backdrop-blur-xl">
      <div className="container flex h-16 items-center gap-4">
        <Logo />

        <nav className="hidden md:flex items-center gap-1 ml-6">
          {NAV.map(({ to, label, icon: Icon }) => {
            const active = location.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                className={cn(
                  "inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors",
                  active
                    ? "bg-secondary text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/60",
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {!titular && <DeliveryUnitSwitcher />}

          {!titular && (
            <>
              <Button
                variant="hero"
                size="default"
                className="hidden sm:inline-flex"
                onClick={() => setCartOpen(true)}
              >
                <ShoppingBag className="h-4 w-4" />
                Carrinho
                <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-foreground/20 px-1.5 text-xs font-semibold">
                  {formatQuantity(cartCount)}
                </span>
              </Button>

              <Button variant="ghost" size="icon" className="sm:hidden" onClick={() => setCartOpen(true)}>
                <ShoppingBag className="h-5 w-5" />
              </Button>
            </>
          )}

          {account && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-2 py-1.5 hover:bg-secondary transition-colors">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gradient-primary text-xs font-semibold text-primary-foreground">
                    {initials}
                  </span>
                  <span className="hidden md:inline text-sm font-medium max-w-[120px] truncate">
                    {account.profile?.companyName ?? account.email}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold truncate">{account.profile?.companyName ?? "Conta"}</span>
                    <span className="text-xs text-muted-foreground truncate">{account.email}</span>
                    <div className="pt-1.5">
                      <StatusBadge status={account.status} />
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/perfil")}>
                  <User className="h-4 w-4" />
                  Minha ficha
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/pedidos")}>
                  <ClipboardList className="h-4 w-4" />
                  Pedidos
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }}>
                  <LogOut className="h-4 w-4" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72">
              <div className="flex flex-col gap-1 mt-8">
                {NAV.map(({ to, label, icon: Icon }) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setMobileOpen(false)}
                    className="inline-flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium hover:bg-secondary"
                  >
                    <Icon className="h-5 w-5" />
                    {label}
                  </Link>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
    </header>
  );
}
